/**
 * Best Run Benchmark calculation.
 *
 * Determines the best historical completed run for a task category by selecting
 * the most meaningful available metric in priority order:
 *   1. Lowest cost per unit  (when units + cost both present on 2+ runs)
 *   2. Lowest labour cost    (when cost is available on 2+ runs, no units)
 *   3. Fastest net duration  (fallback when no cost data)
 *
 * Safeguards:
 * - Runs under 3 minutes are excluded (likely test or bad data)
 * - Runs missing startTime or endTime are excluded
 * - Zero-cost runs where team was assigned are excluded from cost comparison
 * - Minimum 2 valid runs required before a benchmark is returned
 */

const MIN_DURATION_MINUTES = 3;

function isValidRun(task) {
  if (!task.startTime || !task.endTime) return false;
  if (!task.durationMinutes || task.durationMinutes < MIN_DURATION_MINUTES) return false;
  return true;
}

/**
 * Calculate the best run benchmark for a set of tasks from one category group.
 *
 * @param {object[]} tasks - completed, non-archived tasks for a single category
 * @returns {object|null} benchmark result or null if insufficient data
 */
export function calcBenchmark(tasks) {
  const valid = tasks.filter(isValidRun);
  if (valid.length < 2) return null;

  const withCostPerUnit = valid.filter((t) => t.costPerUnit != null && t.costPerUnit > 0);
  const withCost = valid.filter(
    (t) => t.labourCost != null && t.labourCost > 0
  );

  let best = null;
  let primaryMetric = null;

  if (withCostPerUnit.length >= 2) {
    best = withCostPerUnit.reduce((a, b) => (a.costPerUnit < b.costPerUnit ? a : b));
    primaryMetric = 'costPerUnit';
  } else if (withCost.length >= 2) {
    best = withCost.reduce((a, b) => (a.labourCost < b.labourCost ? a : b));
    primaryMetric = 'labourCost';
  } else {
    const withDuration = valid.filter(
      (t) => (t.netDurationMinutes ?? t.durationMinutes) > 0
    );
    if (withDuration.length < 2) return null;
    best = withDuration.reduce((a, b) => {
      const da = a.netDurationMinutes ?? a.durationMinutes;
      const db = b.netDurationMinutes ?? b.durationMinutes;
      return da < db ? a : b;
    });
    primaryMetric = 'duration';
  }

  if (!best) return null;

  const avgDuration =
    valid.reduce((s, t) => s + (t.durationMinutes || 0), 0) / valid.length;
  const avgUnitsPerHour =
    valid.filter((t) => t.unitsPerHour != null).length >= 2
      ? valid.reduce((s, t) => s + (t.unitsPerHour || 0), 0) /
        valid.filter((t) => t.unitsPerHour != null).length
      : null;

  const bestDate =
    best.submittedAt?.toDate?.() ??
    (best.submittedAt ? new Date(best.submittedAt) : null);

  return {
    primaryMetric,
    costPerUnit: best.costPerUnit ?? null,
    labourCost: best.labourCost ?? null,
    labourCostCurrency: best.labourCostCurrency ?? null,
    durationMinutes: best.durationMinutes,
    netDurationMinutes: best.netDurationMinutes ?? null,
    quantityCompleted: best.quantityCompleted ?? null,
    unitType: best.unitType ?? null,
    unitsPerHour: best.unitsPerHour ?? null,
    unitsPerLabourHour: best.unitsPerLabourHour ?? null,
    taskName: best.isCustomTaskName ? best.customTaskName : best.taskName,
    categoryName: best.taskCategoryName ?? null,
    employeeNames: best.employeeNames ?? [],
    teamSize: best.teamSize ?? null,
    date: bestDate,
    taskId: best.id,
    runCount: valid.length,
    avgDurationMinutes: Math.round(avgDuration),
    avgUnitsPerHour,
  };
}

/**
 * Group tasks by category (taskCategoryId for preset tasks, or a custom key
 * for custom-named tasks) and compute a benchmark for each group.
 *
 * @param {object[]} tasks
 * @returns {Map<string, object|null>}
 */
export function calcBenchmarksByCategory(tasks) {
  const groups = new Map();

  for (const task of tasks) {
    const key = task.taskCategoryId || `custom:${task.taskName}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(task);
  }

  const results = new Map();
  for (const [key, groupTasks] of groups) {
    results.set(key, calcBenchmark(groupTasks));
  }
  return results;
}

/**
 * Return the benchmark lookup key for a task — used to find the matching
 * benchmark in the map returned by calcBenchmarksByCategory.
 */
export function getBenchmarkKey(task) {
  return task?.taskCategoryId || `custom:${task?.taskName}`;
}

/**
 * Compute a percentage difference between a candidate run and the benchmark.
 * For duration and cost: positive = worse (over benchmark), negative = better.
 * For unitsPerHour: positive = better (above benchmark), negative = worse.
 *
 * Returns null when the comparison cannot be made.
 */
export function calcBenchmarkComparison(benchmark, task) {
  if (!benchmark || !task) return null;

  switch (benchmark.primaryMetric) {
    case 'costPerUnit': {
      const current = task.costPerUnit;
      if (current == null || benchmark.costPerUnit == null) return null;
      return {
        metric: 'costPerUnit',
        pctDiff: ((current - benchmark.costPerUnit) / benchmark.costPerUnit) * 100,
        betterWhenLower: true,
      };
    }
    case 'labourCost': {
      const current = task.labourCost;
      if (current == null || benchmark.labourCost == null) return null;
      return {
        metric: 'labourCost',
        pctDiff: ((current - benchmark.labourCost) / benchmark.labourCost) * 100,
        betterWhenLower: true,
      };
    }
    default: {
      const bDur = benchmark.netDurationMinutes ?? benchmark.durationMinutes;
      const tDur = task.netDurationMinutes ?? task.durationMinutes;
      if (!bDur || !tDur) return null;
      return {
        metric: 'duration',
        pctDiff: ((tDur - bDur) / bDur) * 100,
        betterWhenLower: true,
      };
    }
  }
}
